import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithCustomToken,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { firebaseAuth } from '../lib/firebase';

/**
 * Deep-link landing page used by the admin app to drop the operator
 * into the main app signed in *as* a target user. The admin SPA opens
 * `https://lexcrm.aryeh.win/auth/impersonate?token=<customToken>` in a
 * new tab; this page exchanges the custom token for a Firebase session
 * (carrying the imp_by claim) and bounces to /.
 *
 * IMPORTANT: switches the auth instance to browserSessionPersistence
 * BEFORE signing in. Firebase's default `local` persistence stores the
 * session in IndexedDB / localStorage and propagates the change to
 * every other tab on the same origin via storage events — meaning if
 * the admin happens to also be signed in to lexcrm.aryeh.win as
 * themselves (their own firm), impersonation would hijack that
 * session. Session persistence keeps the impersonated session strictly
 * inside this tab; closing the tab also signs them out automatically.
 */
export default function ImpersonatePage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Token is in the URL fragment (#token=…). Browsers do NOT send
    // fragments to servers, so nginx/api access logs never see it.
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const token = params.get('token');
    if (!token) {
      setError('missing token');
      return;
    }
    setPersistence(firebaseAuth, browserSessionPersistence)
      .then(() => signInWithCustomToken(firebaseAuth, token))
      .then(() => {
        // Strip the fragment from history so a refresh / share doesn't
        // replay the (short-lived) custom token.
        navigate('/', { replace: true });
      })
      .catch((err) => {
        setError((err as Error).message);
      });
  }, [navigate]);

  return (
    <div style={{ padding: 32, textAlign: 'center', fontFamily: 'system-ui' }}>
      {error ? (
        <>
          <h2 style={{ color: '#b91c1c' }}>Impersonation failed</h2>
          <p>{error}</p>
        </>
      ) : (
        <p>Signing in as target user…</p>
      )}
    </div>
  );
}
